import type { SelectItems, SelectModelValue, SelectOption } from '../SelectTypes'
import { computed, shallowRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { externalOptionValue, isOptionItem } from '../SelectTypes'

interface ValueProps {
  multiple: boolean
  labelProp: string
  valueAsObject: boolean
  labelTextFormatter?: (value: unknown, getDefaultLabelText: (value: unknown) => string) => string
}

export interface SelectionEvents {
  updateModel: (value: SelectModelValue) => void
  inputValue: (value: SelectItems) => void
  clear: () => void
  select: (option: SelectOption) => void
  deselect: (option: SelectOption) => void
}

export default function useValue(props: ValueProps, events: SelectionEvents) {
  // Both modes use an array internally: single selection contains at most one option.
  const committed = shallowRef<SelectOption[]>([])

  function items(options: readonly SelectOption[]): SelectItems {
    return props.multiple ? options.map(option => option.item) : (options[0]?.item ?? null)
  }

  function external(options: readonly SelectOption[]): SelectModelValue {
    return props.multiple
      ? options.map(option => externalOptionValue(option, props.valueAsObject))
      : options[0]
        ? externalOptionValue(options[0], props.valueAsObject)
        : null
  }

  function setInternal(options: SelectOption[]) {
    committed.value = options
    events.inputValue(items(options))
  }

  function update(options: SelectOption[]) {
    setInternal(options)
    events.updateModel(external(options))
  }

  function clear() {
    events.clear()
    update([])
  }

  const hasSelected = computed(() => committed.value.length > 0)
  const i18n = useI18n()
  function getDefaultLabelText(value: unknown): string {
    function label(item: unknown) {
      return isOptionItem(item) ? String(item[props.labelProp] ?? '') : ''
    }
    return Array.isArray(value)
      ? value
          .map(label)
          .map(text => i18n.t(text))
          .join(', ')
      : label(value)
  }
  const labelText = computed(() => {
    const value = items(committed.value)
    return props.labelTextFormatter?.(value, getDefaultLabelText) || getDefaultLabelText(value)
  })

  return { committed, setInternal, update, clear, hasSelected, labelText }
}
